import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isAlpha from 'validator/lib/isAlpha'
import isCurrency from 'validator/lib/isCurrency'
import isDate from 'validator/lib/isDate'
import isEmail from 'validator/lib/isEmail'
import { z } from 'zod'
import submitValidatePhoneNumber from '../shared/lib/client/submitValidatePhoneNumber'
import { invalidStates, stateEnum } from './states'

export const FULL_NAME_TOO_LONG = 'Full name cannot exceed 36 characters'

export const isValidUSPhoneNumber = async (value: string) => {
  try {
    const phoneNumber = `+1${value.replace(/\D/g, '')}`
    const response = await submitValidatePhoneNumber({ phoneNumber })
    return response
  } catch {
    return false
  }
}

export const FIRST_NAME_VALIDATION = z.preprocess(
  (arg) => (arg ? (arg as string).trim() : arg),
  z
    .string({ required_error: 'First name is required' })
    .min(2, { message: 'Must be at least 2 characters' })
    .regex(/^[A-Za-z'-\s]+$/, { message: 'Please enter a valid name' })
)

export const LAST_NAME_VALIDATION = z.preprocess(
  (arg) => (arg ? (arg as string).trim() : arg),
  z
    .string({ required_error: 'Last name is required' })
    .min(2, { message: 'Must be at least 2 characters' })
    .regex(/^[A-Za-z'-\s]+$/, { message: 'Please enter a valid name' })
)

const MIDDLE_INITIAL_VALIDATION = z.preprocess(
  (arg) => (arg === '' ? undefined : arg), // Convert "" to undefined
  z
    .string()
    .max(1, { message: 'Must be a single character' })
    .refine((val) => isAlpha(val), { message: 'Please enter a valid middle initial' })
    .optional()
)

// names are grouped together so we can validate them with superRefine
export const FULL_NAME_VALIDATION = z
  .object({
    firstName: FIRST_NAME_VALIDATION,
    middleInitial: MIDDLE_INITIAL_VALIDATION,
    lastName: LAST_NAME_VALIDATION,
  })
  .superRefine((val, ctx) => {
    const mi = val.middleInitial?.length ? 1 : 0

    if (val.firstName.length + mi + val.lastName.length > 36) {
      ;['firstName', 'lastName', 'middleInitial'].forEach((path) =>
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: FULL_NAME_TOO_LONG,
          path: [path],
        })
      )
    }
    return z.NEVER
  })

export const STREET_ADDRESS_VALIDATION = z
  .string()
  .min(1, { message: 'Address is required' })
  .min(2, { message: 'Must be at least 2 characters' })
  .max(50, { message: 'Must be no more than 50 characters' })
  .regex(/^[a-zA-Z0-9\-/#\s]*$/, { message: 'Must be a valid address' })
  .refine((val) => !/pobox/gi.test(val.replace(/[^a-z0-9]/gi, '')), {
    message: 'Address cannot be a PO Box',
  })

export const UNIT_VALIDATION = z.preprocess(
  (arg) => (arg === '' ? undefined : arg), // Convert "" to undefined
  z
    .string()
    .max(32, { message: 'Must be no more than 32 characters' })
    .regex(/^[a-zA-Z0-9\-/#\s]*$/, { message: 'Must be a valid address' })
    .optional()
)

export const CITY_VALIDATION = z
  .string({ required_error: 'City is required' })
  .min(2, { message: 'Must be at least 2 characters' })
  .regex(/^[a-zA-Z0-9\-/#\s]*$/, { message: 'Must be a valid address' })

export const STATE_VALIDATION = z.enum(stateEnum).superRefine((val, ctx) => {
  if (invalidStates.includes(val)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Sorry, we are not issuing cards in ${val} yet.`,
    })
  }
})

export const ZIP_CODE_VALIDATION = z
  .string({ required_error: 'Zip Code is required' })
  .regex(/^\d{5}$/, { message: 'Must be a valid USA ZIP Code' })

export const DATE_OF_BIRTH_VALIDATION = z
  .string({
    required_error: 'Date of birth is required',
  })
  .refine(
    (val) => {
      try {
        return isDate(val, { format: 'MM/DD/YYYY', strictMode: true })
      } catch {
        return false
      }
    },
    { message: 'Date of birth is invalid' }
  )
  .refine(
    (val) => {
      dayjs.extend(customParseFormat)
      const dateOfBirthValue = dayjs(val, 'MM/DD/YYYY', 'en', true)
      const minDoB = dayjs().subtract(18, 'years').startOf('day')

      const isOlderThan18 = dateOfBirthValue.isBefore(minDoB) || dateOfBirthValue.isSame(minDoB)
      return isOlderThan18
    },
    {
      message: 'Must be at least 18 years old',
    }
  )
  .refine(
    (val) => {
      const dateOfBirthValue = dayjs(val, 'MM/DD/YYYY', 'en', true)
      const maxDoB = dayjs().subtract(120, 'years').startOf('day')

      const isYoungerThan120YearsOld =
        dateOfBirthValue.isAfter(maxDoB) || dateOfBirthValue.isSame(maxDoB)
      return isYoungerThan120YearsOld
    },
    {
      message: 'Must be a valid date of birth',
    }
  )

export const DATE_OF_BIRTH_SIMPLE_VALIDATION = z
  .string({
    required_error: 'Date of birth is required',
  })
  .refine(
    (val) => {
      try {
        return isDate(val, { format: 'MM/DD/YYYY', strictMode: true })
      } catch {
        return false
      }
    },
    { message: 'Date of birth is invalid' }
  )

export const EMAIL_VALIDATION = z
  .string({ required_error: 'Email is required' })
  .min(1, { message: 'Email is required' })
  .refine((val) => /^[\x20-\x7E]+$/.test(val), {
    message: 'Email must not contain special characters',
  })
  .refine((val) => isEmail(val), { message: 'Must be a valid email' })

// email and confirmEmail are grouped together so we can validate them with refine
export const EMAILS_VALIDATION = z
  .object({
    email: EMAIL_VALIDATION,
    confirmEmail: EMAIL_VALIDATION,
  })
  .refine(({ email, confirmEmail }) => email === confirmEmail, {
    message: 'This needs to match Email Address',
    path: ['confirmEmail'],
  })

// maintain schema structure for multi-card single email case
export const EMAIL_MC_VALIDATION = z.object({ email: EMAIL_VALIDATION })

export const PHONE_NUMBER_VALIDATION = z.string({
  required_error: 'Phone number is required',
})

const invalidSsns = ['111-11-1111', '333-33-3333', '666-66-6666', '999-99-9999', '123-45-6789']
export const SSN_VALIDATION = z
  .string({ required_error: 'SSN or ITIN is required' })
  .min(1, { message: 'SSN or ITIN is required' })
  .regex(/^(?!666|000)\d{3}-(?!00)\d{2}-(?!0{4})\d{4}$/, {
    message: 'Must be valid SSN or ITIN',
  })
  .refine((val) => !invalidSsns.includes(val), { message: 'Must be valid SSN or ITIN' })

export const ACCOUNT_VALIDATION = z.enum(['checking', 'savings', 'both', 'neither'])

export const ACCOUNT_BALANCE_VALIDATION = z.string().optional()

export const JOB_ROLE_VALIDATION = z.string().optional()

export const PAYMENT_METHOD_VALIDATION = z.array(z.string()).optional()

export const TIME_AT_CURRENT_JOB_VALIDATION = z.string().optional()

export const UNEMPLOYMENT_DURATION_VALIDATION = z.string().optional()

export const DEGREE_TYPE_VALIDATION = z.string().optional()

export const INSTITUTION_VALIDATION = z.string().optional()

export const FIELD_OF_STUDY_VALIDATION = z.string().optional()

export const JOB_COUNT_VALIDATION = z.string().optional()

export const ADDITIONAL_INCOME_SOURCES_VALIDATION = z.array(z.string()).optional()

export const jobCountAndIncomeRefinement = (
  data: {
    jobCount?: string
    additionalIncomeSources?: string[]
  },
  ctx: z.RefinementCtx
) => {
  if (!data.jobCount) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'This field is required',
      path: ['jobCount'],
    })
  }
  if (!data.additionalIncomeSources || data.additionalIncomeSources.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'This field is required',
      path: ['additionalIncomeSources'],
    })
  }
}

export const BNPL_FREQUENCY_VALIDATION = z.string().optional()

export const BNPL_PURCHASE_TYPE_VALIDATION = z.string().optional()

export const BNPL_LATE_PAYMENTS_VALIDATION = z.string().optional()

export const bnplRefinement = (
  data: {
    bnplFrequency?: string
    bnplPurchaseType?: string
    bnplLatePayments?: string
  },
  ctx: z.RefinementCtx
) => {
  if (!data.bnplFrequency) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'This field is required',
      path: ['bnplFrequency'],
    })
  }
  if (data.bnplFrequency && data.bnplFrequency !== 'never') {
    if (!data.bnplPurchaseType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: ['bnplPurchaseType'],
      })
    }
    if (!data.bnplLatePayments) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: ['bnplLatePayments'],
      })
    }
  }
}

export const RECENT_APPLICATIONS_VALIDATION = z.string().optional()

export const recentApplicationsRefinement = (
  data: { recentApplications?: string },
  ctx: z.RefinementCtx
) => {
  if (!data.recentApplications) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'This field is required',
      path: ['recentApplications'],
    })
  } else if (!/^\d+$/.test(data.recentApplications)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Please enter a valid number',
      path: ['recentApplications'],
    })
  }
}

export const RECENT_ACCOUNTS_OPENED_VALIDATION = z.string().optional()

export const recentAccountsOpenedRefinement = (
  data: { recentAccountsOpened?: string },
  ctx: z.RefinementCtx
) => {
  if (!data.recentAccountsOpened) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'This field is required',
      path: ['recentAccountsOpened'],
    })
  } else if (!/^\d+$/.test(data.recentAccountsOpened)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Please enter a valid number',
      path: ['recentAccountsOpened'],
    })
  }
}

export const CREDIT_REPAIR_VALIDATION = z.string().optional()

export const creditRepairRefinement = (data: { creditRepair?: string }, ctx: z.RefinementCtx) => {
  if (!data.creditRepair) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'This field is required',
      path: ['creditRepair'],
    })
  }
}

export const EDUCATION_LEVEL_VALIDATION = z.string().optional()

export const EDUCATION_FIELD_OF_STUDY_VALIDATION = z.string().optional()

export const educationRefinement = (
  data: {
    educationLevel?: string
    educationFieldOfStudy?: string
  },
  ctx: z.RefinementCtx
) => {
  if (!data.educationLevel) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'This field is required',
      path: ['educationLevel'],
    })
  }
  if (!data.educationFieldOfStudy) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'This field is required',
      path: ['educationFieldOfStudy'],
    })
  }
}

export const studentDetailRefinement = (
  data: {
    employmentStatus?: string
    degreeType?: string
    institution?: string
    fieldOfStudy?: string
  },
  ctx: z.RefinementCtx
) => {
  if (data.employmentStatus === 'student') {
    if (!data.degreeType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: ['degreeType'],
      })
    }
    if (!data.institution || data.institution.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: ['institution'],
      })
    }
    if (!data.fieldOfStudy) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: ['fieldOfStudy'],
      })
    }
  }
}

export const unemploymentDetailRefinement = (
  data: {
    employmentStatus?: string
    unemploymentDuration?: string
  },
  ctx: z.RefinementCtx
) => {
  if (data.employmentStatus === 'unemployed') {
    if (!data.unemploymentDuration) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: ['unemploymentDuration'],
      })
    } else if (!/^\d+$/.test(data.unemploymentDuration)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid number',
        path: ['unemploymentDuration'],
      })
    }
  }
}

export const INDUSTRY_VALIDATION = z.string().optional()

export const SELF_EMPLOYMENT_DURATION_VALIDATION = z.string().optional()

export const WORK_TYPE_VALIDATION = z.array(z.string()).optional()

export const selfEmploymentDetailRefinement = (
  data: {
    employmentStatus?: string
    industry?: string
    selfEmploymentDuration?: string
    workType?: string[]
  },
  ctx: z.RefinementCtx
) => {
  if (data.employmentStatus === 'self_employed') {
    if (!data.industry) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: ['industry'],
      })
    }
    if (!data.selfEmploymentDuration) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: ['selfEmploymentDuration'],
      })
    } else if (!/^\d+$/.test(data.selfEmploymentDuration)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid number',
        path: ['selfEmploymentDuration'],
      })
    }
    if (!data.workType || data.workType.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: ['workType'],
      })
    }
  }
}

export const employmentDetailRefinement = (
  data: {
    employmentStatus?: string
    jobRole?: string
    paymentMethod?: string[]
    timeAtCurrentJob?: string
  },
  ctx: z.RefinementCtx
) => {
  if (data.employmentStatus === 'employed') {
    if (!data.jobRole) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: ['jobRole'],
      })
    }
    if (!data.paymentMethod || data.paymentMethod.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: ['paymentMethod'],
      })
    }
    if (!data.timeAtCurrentJob) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: ['timeAtCurrentJob'],
      })
    } else if (!/^\d+$/.test(data.timeAtCurrentJob)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid number',
        path: ['timeAtCurrentJob'],
      })
    }
  }
}

export const accountBalanceRefinement = (
  data: { accounts?: string; totalAccountBalance?: string },
  ctx: z.RefinementCtx
) => {
  if (data.accounts && data.accounts !== 'neither') {
    if (!data.totalAccountBalance) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: ['totalAccountBalance'],
      })
    } else if (!isCurrency(data.totalAccountBalance)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid number',
        path: ['totalAccountBalance'],
      })
    }
  }
}

export const CASH_ADVANCE_VALIDATION = z.enum(['yes', 'no'])

export const CONSENT_VALIDATION = z.literal(true)

export const CREDIT_PULL_CONSENT_VALIDATION = z.literal(true)

export const EMPLOYMENT_VALIDATION = z.enum([
  'student',
  'employed',
  'self_employed',
  'unemployed',
  'retired',
])

export const HOUSING_VALIDATION = z.enum(['own', 'rent', 'neither'])

export const STATEMENT_VALIDATION = z.boolean()

export const CREDIT_PROTECTION_VALIDATION = z.enum(['yes', 'no'])

export const INCOME_VALIDATION = z
  .string({
    required_error: 'Income is a required field',
  })
  .refine((val) => isCurrency(val), { message: 'Please enter a valid number' })

export const PAYMENT_VALIDATION = z
  .string({
    required_error: 'This field is required',
  })
  .refine((val) => isCurrency(val), { message: 'Please enter a valid number' })

export const FINANCIAL_DOCUMENT_VALIDATION = z.enum([
  'BANK_STATEMENT',
  'BENEFITS',
  'PAYSTUB',
  'STATEMENT_FROM_SOURCE',
  'TAX_1099',
  'TAX_W2',
])

// The following are used for surfacing error/suggestions
export const GENERIC_VALIDATION = z.string().optional() // General Address error message (no input field)

// Suggestions from the Smarty address validation api
export const SUGGESTION_VALIDATION = z
  .object({
    streetAddress: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
  })
  .optional()
